import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import SubareasService from "../../../services/SubareasService";
import CrearSesionService from "../../../services/CreateSesionService";
import ModeracionService from "../../../services/ModeracionService";

import "./style_steps/step2_5.css";

const N_TEMAS = 10;

const Paso3 = ({
  selectores,
  subareas,
  setSubareas,
  temasPorSubarea,
  setTemas,
  onNext,
  onBack,
}) => {
  // ================================
  // Snackbar (sin afectar diseño)
  // ================================
  const [snack, setSnack] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const showSnack = (message, severity = "error") => {
    setSnack({ open: true, severity, message });
  };

  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((p) => ({ ...p, open: false }));
  };

  const getModeracionMessage = (err) => {
    const detail = err?.response?.data?.detail;
    return detail?.message || "Contenido no permitido (malas palabras o +18).";
  };

  const validarModeracion = async ({ tema, objetivo, sub_area_tematica }, severity = "error") => {
    try {
      await ModeracionService.validarContenido({
        tema,
        objetivo: objetivo || "",
        sub_area_tematica,
      });
      return true;
    } catch (err) {
      showSnack(getModeracionMessage(err), severity);
      return false;
    }
  };

  const [temasLocal, setTemasLocal] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingTema, setLoadingTema] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [editCtx, setEditCtx] = useState(null);

  // ================================
  // Resolver subáreas
  // ================================
  const subareasResueltas = useMemo(() => {
    if (Array.isArray(subareas) && subareas.length) return subareas;

    try {
      const savedPaso2 = JSON.parse(localStorage.getItem("seleccionPaso2"));
      if (!savedPaso2?.listaSubareas || !Array.isArray(savedPaso2.seleccionadas)) return [];
      return savedPaso2.listaSubareas.filter((s) =>
        savedPaso2.seleccionadas.includes(s.id)
      );
    } catch {
      return [];
    }
  }, [subareas]);

  // Key de consistencia
  const temasKey = useMemo(() => {
    const ids = subareasResueltas
      .map((s) => s.id)
      .slice()
      .sort((a, b) => a - b);
    return `${selectores.nivel_id}-${selectores.grado_id}-${selectores.area_id}|${ids.join(",")}`;
  }, [selectores, subareasResueltas]);

  // Persistencia
  const persistAll = useCallback(
    (temasActualizados, subareasActualizadas = subareasResueltas) => {
      localStorage.setItem(
        "temasPaso3",
        JSON.stringify({ temasPorSubarea: temasActualizados })
      );
      localStorage.setItem("temasPaso3_key", temasKey);

      let savedPaso2 = {};
      try {
        savedPaso2 = JSON.parse(localStorage.getItem("seleccionPaso2")) || {};
      } catch {
        savedPaso2 = {};
      }

      localStorage.setItem(
        "seleccionPaso2",
        JSON.stringify({
          ...savedPaso2,
          subareas: subareasActualizadas,
          temasPorSubarea: temasActualizados,
          generado: true,
        })
      );

      const keyPaso2 = `${selectores.nivel_id}-${selectores.grado_id}-${selectores.area_id}`;
      localStorage.setItem("seleccionPaso2_key", keyPaso2);
    },
    [subareasResueltas, temasKey, selectores]
  );

  // ✅ valida si el temasPorSubarea del padre corresponde a la selección actual
  const parentMatchesSelection = useMemo(() => {
    if (!temasPorSubarea || !Object.keys(temasPorSubarea).length) return false;
    const idsParent = Object.keys(temasPorSubarea).map(String).sort().join(",");
    const idsSel = subareasResueltas.map((s) => String(s.id)).sort().join(",");
    return idsParent === idsSel;
  }, [temasPorSubarea, subareasResueltas]);

  // ================================
  // Carga inicial: reutiliza o genera
  // ================================
  useEffect(() => {
    if (!subareasResueltas.length) {
      setTemasLocal({});
      return;
    }

    // ✅ Reutiliza temas del padre SOLO si coincide la selección
    if (parentMatchesSelection) {
      setTemasLocal(temasPorSubarea);
      persistAll(temasPorSubarea, subareasResueltas);
      return;
    }

    // Reutiliza temasPaso3 si key coincide
    let saved = null;
    let savedKey = null;
    try {
      saved = JSON.parse(localStorage.getItem("temasPaso3"));
      savedKey = localStorage.getItem("temasPaso3_key");
    } catch {
      saved = null;
      savedKey = null;
    }

    if (saved?.temasPorSubarea && savedKey === temasKey) {
      setTemasLocal(saved.temasPorSubarea);
      setTemas(saved.temasPorSubarea);
      setSubareas(subareasResueltas);
      persistAll(saved.temasPorSubarea, subareasResueltas);
      return;
    }

    // Generar aquí
    const generar = async () => {
      try {
        setLoading(true);

        const temas = {};
        for (const sub of subareasResueltas) {
          const res = await SubareasService.generarTemas(
            N_TEMAS,
            selectores.nivel_id,
            selectores.grado_id,
            selectores.area_id,
            sub.sub_area
          );

          temas[sub.id] = {
            nombre: sub.sub_area,
            nivel: selectores.nivel_nombre,
            grado: selectores.grado_nombre,
            area: selectores.area_nombre,
            temas: (res.resultado || []).map((t) => ({
              ...t,
              id_PSQL: t.id_PSQL,
            })),
          };
        }

        setTemasLocal(temas);
        setTemas(temas);
        setSubareas(subareasResueltas);
        persistAll(temas, subareasResueltas);
      } catch (err) {
        console.error("Error al generar temas:", err);
      } finally {
        setLoading(false);
      }
    };

    generar();
  }, [
    subareasResueltas,
    selectores,
    temasKey,
    parentMatchesSelection,
    temasPorSubarea,
    setTemas,
    setSubareas,
    persistAll,
  ]);

  // ================================
  // Normaliza filas
  // ================================
  const rows = useMemo(() => {
    const out = [];
    for (const [subId, sub] of Object.entries(temasLocal || {})) {
      (sub.temas || []).forEach((t, idx) => {
        out.push({
          subId: Number(subId),
          idx,
          nivel: sub.nivel || selectores.nivel_nombre,
          grado: sub.grado || selectores.grado_nombre,
          area: sub.area || selectores.area_nombre,
          subarea: sub.nombre,
          tema: t.tema,
          descripcion: t.descripcion || "",
          id_PSQL: t.id_PSQL,
        });
      });
    }
    return out;
  }, [temasLocal, selectores]);

  // ================================
  // AGRUPAR por subárea (para Accordion)
  // ================================
  const gruposPorSubarea = useMemo(() => {
    const map = new Map();

    rows.forEach((r) => {
      if (!map.has(r.subId)) {
        map.set(r.subId, { subId: r.subId, subarea: r.subarea, items: [] });
      }
      map.get(r.subId).items.push(r);
    });

    return Array.from(map.values()).sort((a, b) => a.subId - b.subId);
  }, [rows]);

  // ================================
  // Meta arriba
  // ================================
  const metaNivel = selectores?.nivel_nombre || rows?.[0]?.nivel || "";
  const metaGrado = selectores?.grado_nombre || rows?.[0]?.grado || "";
  const metaArea = selectores?.area_nombre || rows?.[0]?.area || "";

  // ================================
  // Regenerar tema individual
  // ================================
  const regenerarTema = async (subId, idx) => {
    const subarea = subareasResueltas.find((s) => s.id === subId);
    if (!subarea) return;

    try {
      setLoadingTema(true);

      const res = await SubareasService.generarTemas(
        1,
        selectores.nivel_id,
        selectores.grado_id,
        selectores.area_id,
        subarea.sub_area
      );

      const nuevo = res.resultado?.[0] || { tema: "Tema nuevo", descripcion: "" };

      // ✅ Moderación sobre lo generado (no afecta diseño)
      const ok = await validarModeracion(
        { tema: nuevo.tema, objetivo: nuevo.descripcion || "", sub_area_tematica: subarea.sub_area },
        "warning"
      );
      if (!ok) return;

      setTemasLocal((prev) => {
        const copia = { ...(prev || {}) };
        const sub = copia[subId];
        if (!sub) return prev;

        const nuevosTemas = [...(sub.temas || [])];
        const prevTema = nuevosTemas[idx] || {};

        nuevosTemas[idx] = { ...nuevo, id_PSQL: prevTema.id_PSQL };
        copia[subId] = { ...sub, temas: nuevosTemas };

        setTemas(copia);
        persistAll(copia, subareasResueltas);
        return copia;
      });
    } catch (err) {
      console.error("Error al regenerar tema:", err);
      showSnack("Error al regenerar tema.", "error");
    } finally {
      setLoadingTema(false);
    }
  };

  // ================================
  // Editar
  // ================================
  const abrirEditar = (subId, idx) => {
    const sub = temasLocal?.[subId];
    const t = sub?.temas?.[idx];
    if (!t) return;

    setEditCtx({
      subId,
      idx,
      tema: t.tema || "",
      descripcion: t.descripcion || "",
    });
    setOpenEdit(true);
  };

  // ✅ Guardar edición + moderación (mantiene UI idéntica)
  const guardarEdicion = async () => {
    if (!editCtx) return;

    const sub = temasLocal?.[editCtx.subId];

    const ok = await validarModeracion(
      {
        tema: editCtx.tema,
        objetivo: editCtx.descripcion || "",
        sub_area_tematica: sub?.nombre || "",
      },
      "error"
    );
    if (!ok) return;

    const { subId, idx, tema, descripcion } = editCtx;

    setTemasLocal((prev) => {
      const copia = { ...(prev || {}) };
      const sub = copia[subId];
      if (!sub) return prev;

      const nuevosTemas = [...(sub.temas || [])];
      const prevTema = nuevosTemas[idx] || {};

      nuevosTemas[idx] = { ...prevTema, tema, descripcion };
      copia[subId] = { ...sub, temas: nuevosTemas };

      setTemas(copia);
      persistAll(copia, subareasResueltas);
      return copia;
    });

    setOpenEdit(false);
    setEditCtx(null);

    // opcional: confirmar guardado
    showSnack("Cambios guardados correctamente.", "success");
  };

  // ================================
  // Registrar SAE -> id_PSQL
  // ================================
  const registrarSesionesSAE = async () => {
    const data = structuredClone(temasLocal || {});
    if (!data || !Object.keys(data).length) return data;

    try {
      setLoading(true);

      for (const [, sub] of Object.entries(data)) {
        for (let i = 0; i < (sub.temas || []).length; i++) {
          const tema = sub.temas[i];
          if (tema.id_PSQL) continue;

          const payload = {
            tipo_sesion: "SAE",
            nivel_id: selectores.nivel_id,
            grado_id: selectores.grado_id,
            area_tematica_id: selectores.area_id,
            tema: tema.tema,
            objetivo: tema.descripcion || "",
            estado: "guardado",
            sub_area_tematica: sub.nombre,
            subtipo_sae: "GENERAL",
          };

          // ✅ Moderación antes de crear en BD
          const ok = await validarModeracion(
            {
              tema: payload.tema,
              objetivo: payload.objetivo,
              sub_area_tematica: payload.sub_area_tematica,
            },
            "error"
          );

          if (!ok) {
            // política: detener para que corrijan (recomendado)
            throw new Error("Bloqueado por moderación");
          }

          const sesionBD = await CrearSesionService.crearSesionSAE(payload);

          const nuevoId =
            sesionBD?.id ??
            sesionBD?.data?.id ??
            sesionBD?.response?.data?.id;

          if (!nuevoId) {
            console.error("No llegó id_PSQL. Respuesta:", sesionBD);
            showSnack("No se pudo registrar una sesión (no llegó ID).", "warning");
            continue;
          }

          sub.temas[i] = { ...tema, id_PSQL: nuevoId };
        }
      }

      setTemasLocal(data);
      setTemas(data);
      setSubareas(subareasResueltas);
      persistAll(data, subareasResueltas);

      return data;
    } catch (error) {
      console.error("❌ Error al registrar sesiones SAE:", error);
      // si fue moderación, ya avisamos por snack; para otros errores, aviso general
      if (String(error?.message || "").toLowerCase().includes("moderación") === false) {
        showSnack("Error al registrar sesiones SAE.", "error");
      }
      return data;
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // Continuar
  // ================================
  const handleContinuar = async () => {
    const yaRegistrado =
      temasLocal &&
      Object.values(temasLocal).every((sub) =>
        (sub.temas || []).every((t) => t.id_PSQL)
      );

    const actualizado = yaRegistrado ? temasLocal : await registrarSesionesSAE();

    // Si hay bloqueos/pedientes, no avanzamos
    const todoRegistrado =
      actualizado &&
      Object.values(actualizado).every((sub) =>
        (sub.temas || []).every((t) => t.id_PSQL)
      );

    if (!todoRegistrado) {
      showSnack("No se puede continuar: hay contenidos bloqueados o pendientes.", "warning");
      return;
    }

    setTemas(actualizado);
    setSubareas(subareasResueltas);
    persistAll(actualizado, subareasResueltas);

    onNext();
  };

  return (
    <Box className="step25-root" sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Paso 3: Tabla consolidada de subáreas y sesiones de aprendizaje
      </Typography>

      {/* META arriba */}
      <Box className="stepX-meta">
        <Typography className="stepX-meta-row">
          <span className="stepX-meta-label">Nivel:</span>
          <span className="stepX-meta-value">{metaNivel}</span>
        </Typography>
        <Typography className="stepX-meta-row">
          <span className="stepX-meta-label">Grado:</span>
          <span className="stepX-meta-value">{metaGrado}</span>
        </Typography>
        <Typography className="stepX-meta-row">
          <span className="stepX-meta-label">Área Temática:</span>
          <span className="stepX-meta-value">{metaArea}</span>
        </Typography>
      </Box>

      {/* Un Accordion por Subárea */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {gruposPorSubarea.map((g, i) => (
          <Accordion
            key={g.subId}
            defaultExpanded={i === 0}
            className="sae-subarea-accordion"
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className="sae-subarea-summary"
            >
              <Typography className="sae-subarea-summary-text">
                Subárea: {g.subarea}
              </Typography>
            </AccordionSummary>

            <AccordionDetails className="sae-subarea-details">
              <TableContainer component={Paper} className="sae-subarea-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sesión de Aprendizaje</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {g.items.map((r) => (
                      <TableRow key={`${r.subId}-${r.idx}`}>
                        <TableCell>{r.tema}</TableCell>
                        <TableCell sx={{ whiteSpace: "normal" }}>
                          {r.descripcion || "—"}
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => regenerarTema(r.subId, r.idx)}
                              disabled={loadingTema || loading}
                            >
                              Regenerar
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => abrirEditar(r.subId, r.idx)}
                              disabled={loadingTema || loading}
                            >
                              Editar
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Botonera (Atrás izq / Continuar der) */}
      <Box className="step25-footer">
        <Button variant="outlined" onClick={onBack}>
          Atrás
        </Button>

        <Button
          variant="contained"
          onClick={handleContinuar}
          disabled={loading || loadingTema || rows.length === 0}
        >
          Continuar
        </Button>
      </Box>

      {/* MODAL EDITAR */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="md">
        <DialogTitle>Editar sesión y descripción</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Sesión de Aprendizaje"
            sx={{ mb: 2, mt: 2, }}
            value={editCtx?.tema || ""}
            onChange={(e) => setEditCtx((p) => ({ ...p, tema: e.target.value }))}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Descripción"
            value={editCtx?.descripcion || ""}
            onChange={(e) => setEditCtx((p) => ({ ...p, descripcion: e.target.value }))}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpenEdit(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardarEdicion}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* LOADING OVERLAY */}
      <Backdrop
        open={loading || loadingTema}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* SNACKBAR (no cambia diseño del resto) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Paso3;
